import { create } from "zustand";
import { hubBackHref } from "../lib/hostConfig";
import type {
  Beat,
  ExposureDimension,
  OutcomeEvent,
  OutcomeType,
  ScenarioSpec,
  ScoringEvent,
} from "../scenario/types";
import type { Capability } from "../lib/capability";

/**
 * The runtime engine + store. Holds scenario progression (beat graph),
 * outcome events (the ONLY source of truth for scoring), live transcript,
 * and derived score. Kept framework-light so it ports into Cyberfrog later.
 */

export interface TranscriptLine {
  id: string;
  role: "attacker" | "learner";
  text: string;
  final: boolean;
  at: number;
}

/** One decision the Risk Meter has scored so far, for the expandable
 *  "learning card" view. Mirrors the matching ScoringEvent, dedup'd and
 *  timestamped by when the outcome that triggered it was recorded. */
export interface RiskMeterItem {
  id: string;
  label: string;
  kind: "risk" | "positive";
  weight: number;
  detail?: string;
  at: number;
}

/** One dimension's tally on the Attack Exposure Map. */
export interface ExposureMeterDim {
  dimension: ExposureDimension;
  label: string;
  percent: number;
}

/** One learner decision worth remembering on the debrief's "what happened"
 *  log — a `ScoringEvent` marked `collectEvidence: true` that actually fired. */
export interface EvidenceItem {
  id: string;
  label: string;
  kind: "risk" | "positive";
  detail?: string;
  evidence?: string;
  at: number;
}

const EXPOSURE_LABELS: Record<ExposureDimension, string> = {
  identity: "Identity",
  information: "Information",
  money: "Money",
  operations: "Operations",
};

/** One row for the "Course chapters" disclosure under the Attack Exposure
 *  Map (Change 10). `reached` gates clickability; `current` highlights
 *  whichever chapter the learner (or an active review) is inside right now. */
export interface ChapterItem {
  id: string;
  title: string;
  beatId: string;
  reached: boolean;
  current: boolean;
}

interface SimState {
  scenario: ScenarioSpec | null;
  capability: Capability | null;

  currentBeatId: string | null;
  /** which device shell currently has control (null = pure 3D). */
  activeDeviceId: string | null;

  outcomes: OutcomeEvent[];
  transcript: TranscriptLine[];

  phase: "boot" | "ready" | "running" | "debrief" | "done";

  /** Every beat id the learner's REAL playthrough has actually visited (not
   *  touched while reviewing) — powers "which chapters are reached yet". */
  visitedBeatIds: Set<string>;
  /** Non-null while the learner is inside a read-only chapter review
   *  (Change 10). The beat id they jumped FROM/TO for review viewing. */
  reviewFromBeatId: string | null;
  /** Saved real position to restore on exit — set the moment review starts. */
  reviewReturn: { beatId: string; phase: SimState["phase"]; activeDeviceId: string | null } | null;
  /** The borrowed scene's next beat is outside this visit. Show the way back. */
  sliceDone: boolean;
  /** Non-null while a "What would have happened" replay runs: the learner
   *  made the safe choice on a beat whose safe teach card sets `teach.whatIf`,
   *  and the course now plays the attack they stopped. Cleared at the quiz,
   *  debrief or intro. */
  whatIf: string | null;

  // --- lifecycle ---
  init: (scenario: ScenarioSpec, capability: Capability) => void;
  start: () => void;

  // --- beat progression ---
  currentBeat: () => Beat | null;
  advance: (outcomeKey?: string) => void;
  /** Jump to a beat by id (room waypoint hops). Does not walk edges. */
  goToBeat: (beatId: string) => void;
  /** Course chapters (Change 10), derived from `scenario.chapters` +
   *  `visitedBeatIds`. Empty array for courses that don't set `chapters`. */
  chapters: () => ChapterItem[];
  /** Enter a READ-ONLY review of an already-reached chapter: temporarily
   *  renders from that chapter's beat, suppresses `recordOutcome` writes, and
   *  remembers the real position to snap back to. No-op if already reviewing
   *  or the beat doesn't exist. */
  enterReview: (beatId: string) => void;
  /** Exit review and restore the exact real position + phase + device. */
  exitReview: () => void;
  /** Swap in a re-localized (or original English) copy of the SAME scenario
   *  (identical beat ids/edges — only leaf strings differ), without touching
   *  progression state. Used ONLY by the intro-card language selector, while
   *  the learner is still on beat 0 and nothing has been scored yet (see
   *  `lib/locale.ts::applyLocaleOverlay`). */
  setScenario: (scenario: ScenarioSpec) => void;

  // --- device control ---
  setActiveDevice: (id: string | null) => void;

  // --- outcomes + scoring ---
  recordOutcome: (e: Omit<OutcomeEvent, "at">) => void;
  score: () => { raw: number; passed: boolean; risk: "low" | "medium" | "high" };
  /** 0–100 risk meter for courses that opt in via voiceAttackers scoring weights.
   *  `items` is the growing, spoiler-free log of decisions scored so far
   *  (newest first) that powers the meter's expandable learning-card view. */
  riskMeter: () => { percent: number; enabled: boolean; items: RiskMeterItem[] };
  /** Attack Exposure Map: four dimension tallies (0-100 each) for courses that
   *  opt in via `scenario.exposureMap`. Replaces a single point score with
   *  "what kind of harm did this expose" — Identity/Information/Money/Ops. */
  exposureMap: () => { enabled: boolean; dims: ExposureMeterDim[] };
  /** The learner's own "what happened" evidence log — every fired
   *  ScoringEvent marked `collectEvidence: true`, oldest first. */
  evidence: () => EvidenceItem[];
  /** Episode-12-style branching finale selector: "A" (attack interrupted —
   *  no risk event ever fired), "B" (some risky choices but nothing
   *  catastrophic), or "C" (major incident — full compromise or an
   *  irreversible loss like a paid wire/ransom). Deliberately independent
   *  of whether the course's final incident report was filed: in a
   *  strictly linear course that beat is mandatory, so "was it reported"
   *  can never distinguish playthroughs. Generic — reads only
   *  score()/outcomes, no per-course hardcoding, so any future course
   *  gets a working finale for free. */
  endingBranch: () => "A" | "B" | "C";

  // --- transcript (voice) ---
  pushTranscript: (line: Omit<TranscriptLine, "at">) => void;
  updateTranscript: (id: string, text: string, final: boolean) => void;
  /** Wipe the shared call transcript. MUST be called by every call-style
   *  device (VideoCall/PhoneCall/TypedCall/DeepfakeCall/useVoiceCall) at the
   *  moment ITS OWN call starts — `transcript` is one global array shared by
   *  every call-like device in a course (2026-07-26 regression: the earlier
   *  Teams deepfake call's lines were still sitting in this array when the
   *  live vishing call started later in the same story, so its chat log
   *  rendered old Diane-Okafor lines above the new live AI conversation).
   *  Any NEW call-style device must call this on mount/start too. */
  clearTranscript: () => void;

  /** Learner opt-in for a `freeRoamGated` beat (see types.ts) — checked via a
   *  checkbox on an earlier `offerFreeRoamCheckbox` intro beat. Defaults to
   *  false every playthrough (not persisted), matching the "default to
   *  look-only, walking is an explicit choice" design. */
  walkOptIn: boolean;
  setWalkOptIn: (v: boolean) => void;

  reset: () => void;
}

/** Beat ids named in `?stop=` when this visit must not continue into them.
 *  Used when the Social Engineering terminal borrows one scene from another course. */
export function sliceStopIds(): Set<string> {
  try {
    const raw = new URLSearchParams(window.location.search).get("stop") ?? "";
    return new Set(
      raw
        .split(",")
        .map((id) => id.trim())
        .filter((id) => /^[A-Za-z0-9_-]{1,64}$/.test(id)),
    );
  } catch {
    return new Set();
  }
}

export function sliceStopsNext(scenario: ScenarioSpec, fromId: string, outcomeKey?: string): boolean {
  const stop = sliceStopIds();
  if (stop.size === 0) return false;
  if (!new URLSearchParams(window.location.search).get("return")) return false;
  const to = nextBeatId(scenario, fromId, outcomeKey);
  return !!to && stop.has(to);
}

function nextBeatId(scenario: ScenarioSpec, fromId: string, outcomeKey?: string): string | null {
  const edges = scenario.edges.filter((e) => e.from === fromId);
  if (edges.length === 0) return null;
  if (outcomeKey) {
    // "verify:asked" refines "verify": a course without its own edge for the
    // refined key takes the plain one.
    const keys = [outcomeKey, outcomeKey.split(":")[0]];
    for (const k of keys) {
      const matched = edges.find((e) => e.on === k);
      if (matched) return matched.to;
    }
  }
  const def = edges.find((e) => !e.on || e.on === "default") ?? edges[0];
  return def.to;
}

/** A beat where the learner makes a real choice (a `safeChoice` or keyed
 *  edges). Choosing there ends a what-if replay. */
function offersSafePath(scenario: ScenarioSpec | null, beat: Beat | undefined): boolean {
  if (!beat) return false;
  return !!beat.safeChoice || !!scenario?.edges.some((e) => e.from === beat.id && !!e.on);
}

/** The replay banner stays on a decision screen (it explains why that screen
 *  exists) and ends once the learner has chosen there; a safe choice's teach
 *  card then starts a new replay. */
function nextWhatIf(current: Beat | undefined, next: Beat | undefined, prev: string | null, currentDecides = false): string | null {
  if (!next || next.type === "quiz" || next.type === "debrief" || next.type === "intro") return null;
  if (next.endsWhatIf) return null;
  if (current?.type === "teach" && current.teach?.whatIf) return current.teach.whatIf;
  if (currentDecides) return null;
  return prev;
}

export const useSim = create<SimState>((set, get) => ({
  scenario: null,
  capability: null,
  currentBeatId: null,
  activeDeviceId: null,
  outcomes: [],
  transcript: [],
  phase: "boot",
  visitedBeatIds: new Set<string>(),
  reviewFromBeatId: null,
  reviewReturn: null,
  walkOptIn: false,
  sliceDone: false,
  whatIf: null,

  init: (scenario, capability) =>
    set({
      scenario,
      capability,
      currentBeatId: null,
      phase: "ready",
      outcomes: [],
      transcript: [],
      visitedBeatIds: new Set<string>(),
      reviewFromBeatId: null,
      reviewReturn: null,
      walkOptIn: false,
      sliceDone: false,
      whatIf: null,
    }),

  start: () => {
    const { scenario } = get();
    if (!scenario) return;
    set((s) => ({
      currentBeatId: scenario.initialBeatId,
      phase: "running",
      visitedBeatIds: new Set(s.visitedBeatIds).add(scenario.initialBeatId),
    }));
  },

  currentBeat: () => {
    const { scenario, currentBeatId } = get();
    if (!scenario || !currentBeatId) return null;
    return scenario.beats.find((b) => b.id === currentBeatId) ?? null;
  },

  advance: (outcomeKey) => {
    const { scenario, currentBeatId, reviewFromBeatId, reviewReturn } = get();
    if (!scenario || !currentBeatId) return;
    const to = nextBeatId(scenario, currentBeatId, outcomeKey);
    if (to && sliceStopsNext(scenario, currentBeatId, outcomeKey)) {
      try {
        const ret = new URLSearchParams(window.location.search).get("return");
        if (ret && /^[a-z0-9-]{1,64}$/.test(ret)) {
          window.location.assign(hubBackHref(ret));
          return;
        }
      } catch {
        /* ignore */
      }
    }
    if (!to) {
      if (reviewFromBeatId) {
        get().exitReview();
        return;
      }
      set({ phase: "done" });
      return;
    }
    // Reviewing: never let the read-only replay walk PAST the real position
    // (beat-array order, not just an exact id match, so a branch taken
    // differently during the real playthrough still snaps back safely) —
    // restore instead of rendering beats the learner hasn't actually reached.
    if (reviewFromBeatId && reviewReturn) {
      const idxTo = scenario.beats.findIndex((b) => b.id === to);
      const idxReturn = scenario.beats.findIndex((b) => b.id === reviewReturn.beatId);
      if (idxReturn !== -1 && idxTo >= idxReturn) {
        get().exitReview();
        return;
      }
    }
    const beat = scenario.beats.find((b) => b.id === to);
    const from = scenario.beats.find((b) => b.id === currentBeatId);
    set((s) => ({
      currentBeatId: to,
      phase: beat?.type === "debrief" ? "debrief" : "running",
      // device beats own the device; non-device beats release it.
      activeDeviceId: beat?.type === "device" ? beat.deviceId ?? null : null,
      visitedBeatIds: s.reviewFromBeatId ? s.visitedBeatIds : new Set(s.visitedBeatIds).add(to),
      whatIf: s.reviewFromBeatId ? s.whatIf : nextWhatIf(from, beat, s.whatIf, offersSafePath(scenario, from)),
    }));
  },

  goToBeat: (beatId) => {
    const { scenario } = get();
    if (!scenario) return;
    const beat = scenario.beats.find((b) => b.id === beatId);
    if (!beat) return;
    set((s) => ({
      currentBeatId: beatId,
      phase: beat.type === "debrief" ? "debrief" : "running",
      activeDeviceId: beat.type === "device" ? beat.deviceId ?? null : null,
      visitedBeatIds: s.reviewFromBeatId ? s.visitedBeatIds : new Set(s.visitedBeatIds).add(beatId),
      whatIf: s.reviewFromBeatId ? s.whatIf : nextWhatIf(undefined, beat, s.whatIf),
    }));
  },

  chapters: () => {
    const { scenario, visitedBeatIds, currentBeatId, reviewFromBeatId } = get();
    if (!scenario?.chapters?.length) return [];
    const activeId = reviewFromBeatId ?? currentBeatId;
    return scenario.chapters.map((c) => ({
      ...c,
      reached: visitedBeatIds.has(c.beatId),
      current: c.beatId === activeId,
    }));
  },

  enterReview: (beatId) => {
    const { scenario, currentBeatId, phase, activeDeviceId, reviewFromBeatId, visitedBeatIds } = get();
    if (!scenario || !currentBeatId || reviewFromBeatId) return; // ignore nested entry
    if (!visitedBeatIds.has(beatId)) return; // safety net: never preview unreached content
    const beat = scenario.beats.find((b) => b.id === beatId);
    if (!beat) return;
    set({
      reviewFromBeatId: beatId,
      reviewReturn: { beatId: currentBeatId, phase, activeDeviceId },
      currentBeatId: beatId,
      phase: beat.type === "debrief" ? "debrief" : "running",
      activeDeviceId: beat.type === "device" ? beat.deviceId ?? null : null,
    });
  },

  exitReview: () => {
    const { reviewReturn } = get();
    if (!reviewReturn) {
      set({ reviewFromBeatId: null, reviewReturn: null });
      return;
    }
    set({
      currentBeatId: reviewReturn.beatId,
      phase: reviewReturn.phase,
      activeDeviceId: reviewReturn.activeDeviceId,
      reviewFromBeatId: null,
      reviewReturn: null,
    });
  },

      setScenario: (scenario) => set({ scenario }),

      setActiveDevice: (id) => set({ activeDeviceId: id }),

  recordOutcome: (e) => {
    // Read-only review (Change 10) must never mutate real scoring/evidence.
    if (get().reviewFromBeatId) return;
    const { scenario, whatIf } = get();
    if (whatIf && e.eventId && scenario) {
      const beat = scenario.beats.find((b) => b.id === e.beatId);
      const isRisk = Object.values(scenario.voiceAttackers).some((va) =>
        va.scoringEvents.some((ev) => ev.id === e.eventId && ev.kind === "risk"),
      );
      if (beat?.forced && isRisk) e = { ...e, eventId: undefined };
    }
    set((s) => ({ outcomes: [...s.outcomes, { ...e, at: Date.now() }] }));
  },

  score: () => {
    const { scenario, outcomes } = get();
    if (!scenario) return { raw: 0, passed: false, risk: "low" as const };
    // Build an id->ScoringEvent map across all voice attackers.
    const events = new Map<string, ScoringEvent>();
    for (const va of Object.values(scenario.voiceAttackers)) {
      for (const ev of va.scoringEvents) events.set(ev.id, ev);
    }
    let risk = 0;
    let positive = 0;
    let fullCompromise = false;
    let partial = false;
    for (const o of outcomes) {
      if (!o.eventId) continue;
      const ev = events.get(o.eventId);
      if (!ev) continue;
      if (ev.kind === "risk") {
        risk += ev.weight;
        if (ev.weight >= 60) fullCompromise = true;
        else partial = true;
      } else {
        positive += ev.weight;
      }
    }
    // Learner score: start at 100, subtract risk, add back positive (capped).
    const raw = Math.max(0, Math.min(100, 100 - risk + Math.min(positive, 50)));
    const passed = raw >= scenario.passThreshold && !fullCompromise;
    const riskLevel: "low" | "medium" | "high" = fullCompromise ? "high" : partial ? "medium" : "low";
    return { raw, passed, risk: riskLevel };
  },

  riskMeter: () => {
    const { scenario, outcomes } = get();
    if (!scenario?.riskMeter) return { percent: 0, enabled: false, items: [] };
    const events = new Map<string, ScoringEvent>();
    for (const va of Object.values(scenario.voiceAttackers)) {
      for (const ev of va.scoringEvents) events.set(ev.id, ev);
    }
    let risk = 0;
    let positive = 0;
    const seen = new Set<string>();
    const items: RiskMeterItem[] = [];
    for (const o of outcomes) {
      if (!o.eventId || seen.has(o.eventId)) continue;
      seen.add(o.eventId);
      const ev = events.get(o.eventId);
      if (!ev) continue;
      if (ev.kind === "risk") risk += ev.weight;
      else positive += ev.weight;
      items.push({ id: ev.id, label: ev.label, kind: ev.kind, weight: ev.weight, detail: ev.detail, at: o.at });
    }
    items.reverse(); // newest decision first -- the learner just made it, show it up top.
    const percent = Math.max(0, Math.min(100, risk - Math.min(positive, 40)));
    return { percent, enabled: true, items };
  },

  exposureMap: () => {
    const { scenario, outcomes } = get();
    if (!scenario?.exposureMap) return { enabled: false, dims: [] };
    const events = new Map<string, ScoringEvent>();
    for (const va of Object.values(scenario.voiceAttackers)) {
      for (const ev of va.scoringEvents) events.set(ev.id, ev);
    }
    const risk: Record<ExposureDimension, number> = { identity: 0, information: 0, money: 0, operations: 0 };
    const positive: Record<ExposureDimension, number> = { identity: 0, information: 0, money: 0, operations: 0 };
    const seen = new Set<string>();
    for (const o of outcomes) {
      if (!o.eventId || seen.has(o.eventId)) continue;
      seen.add(o.eventId);
      const ev = events.get(o.eventId);
      if (!ev) continue;
      const dim = ev.dimension ?? "operations";
      if (ev.kind === "risk") risk[dim] += ev.weight;
      else positive[dim] += ev.weight;
    }
    const dims: ExposureMeterDim[] = (["identity", "information", "money", "operations"] as const).map((d) => ({
      dimension: d,
      label: EXPOSURE_LABELS[d],
      percent: Math.max(0, Math.min(100, risk[d] - Math.min(positive[d], 40))),
    }));
    return { enabled: true, dims };
  },

  evidence: () => {
    const { scenario, outcomes } = get();
    if (!scenario) return [];
    const events = new Map<string, ScoringEvent>();
    for (const va of Object.values(scenario.voiceAttackers)) {
      for (const ev of va.scoringEvents) events.set(ev.id, ev);
    }
    const seen = new Set<string>();
    const items: EvidenceItem[] = [];
    for (const o of outcomes) {
      if (!o.eventId || seen.has(o.eventId)) continue;
      const ev = events.get(o.eventId);
      if (!ev?.collectEvidence) continue;
      seen.add(o.eventId);
      items.push({ id: ev.id, label: ev.label, kind: ev.kind, detail: ev.detail, evidence: o.evidence, at: o.at });
    }
    return items;
  },

  endingBranch: () => {
    const { scenario, outcomes } = get();
    if (!scenario) return "A";
    // NOTE: deliberately does NOT look at "was it reported" — in a strictly
    // linear course the final incident-report beat is mandatory, so every
    // playthrough reports by the end regardless of earlier choices. That
    // made "reported" useless as a gate (it silently swallowed the C branch
    // for a wire-fraud payout, which must always read as a major incident
    // no matter what happens afterward). The branch is driven purely by
    // whether any single irreversible mistake happened, or by the overall
    // aggregate risk level — both already generic score()/outcomes signals.
    const events = new Map<string, ScoringEvent>();
    for (const va of Object.values(scenario.voiceAttackers)) {
      for (const ev of va.scoringEvents) events.set(ev.id, ev);
    }
    const irreversible = outcomes.some((o) => {
      const ev = o.eventId ? events.get(o.eventId) : undefined;
      return ev?.kind === "risk" && ev.weight >= 55;
    });
    const s = get().score();
    if (irreversible || s.risk === "high") return "C";
    if (s.risk === "low") return "A";
    return "B";
  },

  pushTranscript: (line) =>
    set((s) => ({ transcript: [...s.transcript, { ...line, at: Date.now() }] })),

  updateTranscript: (id, text, final) =>
    set((s) => ({
      transcript: s.transcript.map((l) => (l.id === id ? { ...l, text, final } : l)),
    })),

  clearTranscript: () => set({ transcript: [] }),

  setWalkOptIn: (v) => set({ walkOptIn: v }),

  reset: () =>
    set({
      currentBeatId: null,
      activeDeviceId: null,
      outcomes: [],
      transcript: [],
      phase: "ready",
      visitedBeatIds: new Set<string>(),
      reviewFromBeatId: null,
      reviewReturn: null,
      walkOptIn: false,
      sliceDone: false,
      whatIf: null,
    }),
}));

/** Convenience for the voice hook to translate a detected event into an outcome. */
export function outcomeFromScoringEvent(
  beatId: string,
  ev: ScoringEvent,
  evidence?: string
): Omit<OutcomeEvent, "at"> {
  const type: OutcomeType =
    ev.kind === "positive"
      ? ev.id === "verified_caller"
        ? "verified_caller"
        : "refusal"
      : "disclosure";
  return { type, beatId, eventId: ev.id, evidence };
}
